import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Users, Camera, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api, { resolveFileUrl } from '../utils/api';

const POSITION_OPTIONS = [
  { value: '', label: 'Select position' },
  { value: 'manager', label: 'Manager' },
  { value: 'head_coach', label: 'Head Coach' },
  { value: 'recruitment', label: 'Recruitment' },
  { value: 'sporting_director', label: 'Sporting Director' },
  { value: 'academy_director', label: 'Academy Director' },
  { value: 'director_of_football', label: 'Director of Football' },
  { value: 'chief_scout', label: 'Chief Scout' },
  { value: 'ceo', label: 'CEO' },
  { value: 'chairman', label: 'Chairman' },
  { value: 'owner', label: 'Owner' },
  { value: 'agent_liaison', label: 'Agent Liaison' },
  { value: 'other', label: 'Other' }
];

const LEAGUE_OPTIONS = [
  { value: '', label: 'Select league' },
  { value: 'WSL 1', label: 'WSL 1' },
  { value: 'WSL 2', label: 'WSL 2' },
  { value: 'Tier 3', label: 'Tier 3' },
  { value: 'Tier 4', label: 'Tier 4' },
  { value: 'Tier 5', label: 'Tier 5' },
  { value: 'SWPL', label: 'SWPL' },
  { value: 'NWSL', label: 'NWSL' },
  { value: 'USL Super', label: 'USL Super' },
  { value: 'EUR', label: 'EUR' },
  { value: 'AUS', label: 'AUS' },
  { value: 'ROW', label: 'ROW' },
  { value: 'Other', label: 'Other' }
];

const emptyContact = { name: '', position: '', email: '', phone: '' };

const ClubForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const badgeInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    league: '',
    contact_person: '',
    contact_position: '',
    contact_email: '',
    contact_phone: '',
    contacts: [],
    transfer_budget: '',
    relationship_status: 'new',
    notes: '',
    badge_url: ''
  });

  useEffect(() => {
    if (isEdit) {
      fetchClub();
    }
  }, [id]);

  const fetchClub = async () => {
    try {
      const response = await api.get(`/clubs/${id}`);
      const data = response.data;
      setFormData({
        ...data,
        contacts: data.contacts || [],
        transfer_budget: data.transfer_budget || '',
        badge_url: data.badge_url || ''
      });
    } catch (error) {
      toast.error('Failed to load club');
      navigate('/clubs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        transfer_budget: formData.transfer_budget ? parseFloat(formData.transfer_budget) : null,
        contacts: formData.contacts.filter(c => c.name.trim() !== '')
      };

      if (isEdit) {
        await api.put(`/clubs/${id}`, payload);
        toast.success('Club updated successfully');
      } else {
        await api.post('/clubs', payload);
        toast.success('Club created successfully');
      }
      navigate('/clubs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save club');
    } finally {
      setLoading(false);
    }
  };

  const addContact = () => {
    if (formData.contacts.length < 8) {
      setFormData({
        ...formData,
        contacts: [...formData.contacts, { ...emptyContact }]
      });
    } else {
      toast.error('Maximum 8 contacts allowed');
    }
  };

  const removeContact = (index) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, i) => i !== index)
    });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setFormData({ ...formData, contacts: newContacts });
  };

  const swapPrimaryContact = (index) => {
    if (index === '') return;
    const idx = parseInt(index);
    const selectedContact = formData.contacts[idx];
    const oldPrimary = {
      name: formData.contact_person || '',
      position: formData.contact_position || '',
      email: formData.contact_email || '',
      phone: formData.contact_phone || ''
    };
    const newContacts = [...formData.contacts];
    if (oldPrimary.name) {
      newContacts[idx] = oldPrimary;
    } else {
      newContacts.splice(idx, 1);
    }
    setFormData({
      ...formData,
      contact_person: selectedContact.name || '',
      contact_position: selectedContact.position || '',
      contact_email: selectedContact.email || '',
      contact_phone: selectedContact.phone || '',
      contacts: newContacts
    });
    toast.success(`${selectedContact.name} set as primary contact`);
  };

  const handleBadgeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size must be less than 5MB'); return; }
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const res = await api.post('/upload/badge', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, badge_url: res.data.url });
      toast.success('Badge uploaded');
    } catch (error) {
      toast.error('Failed to upload badge');
    }
    if (badgeInputRef.current) badgeInputRef.current.value = '';
  };

  const removeBadge = () => {
    setFormData({ ...formData, badge_url: '' });
  };

  const relationshipStatuses = ['new', 'good', 'excellent', 'strained'];

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/clubs"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Clubs</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            {isEdit ? 'Edit Club' : 'Add New Club'}
          </h1>
          <p className="text-lg text-zinc-300">Enter club details below</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="glass-card p-8 space-y-6">
            {/* Basic Info */}
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Club Name *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="club-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., Manchester United"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Club Badge
                  </label>
                  <div className="flex gap-4 items-start">
                    <div className="relative">
                      {formData.badge_url ? (
                        <div className="relative group">
                          <div className="w-20 h-20 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden border-2 border-white/10">
                            <img src={resolveFileUrl(formData.badge_url)} alt="Badge" className="max-w-full max-h-full object-contain" />
                          </div>
                          <button type="button" onClick={removeBadge} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-zinc-900/50 border-2 border-dashed border-white/20 flex flex-col items-center justify-center">
                          <Shield className="w-8 h-8 text-zinc-600 mb-1" />
                          <span className="text-[10px] text-zinc-500">No badge</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <input type="file" ref={badgeInputRef} accept="image/*" onChange={handleBadgeUpload} className="hidden" id="badge-upload" />
                      <label htmlFor="badge-upload" data-testid="club-badge-upload-button"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white hover:bg-white/30 rounded-md font-bold cursor-pointer text-sm"
                      >
                        <Camera className="w-4 h-4" />
                        {formData.badge_url ? 'Change Badge' : 'Upload Badge'}
                      </label>
                      <p className="text-xs text-zinc-500 mt-2">Upload club badge/logo image - Max 5MB</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="club-country-input"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., England"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    League *
                  </label>
                  <select
                    required
                    data-testid="club-league-input"
                    value={formData.league}
                    onChange={(e) => setFormData({ ...formData, league: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    {LEAGUE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Relationship Status
                  </label>
                  <select
                    value={formData.relationship_status}
                    onChange={(e) => setFormData({ ...formData, relationship_status: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    {relationshipStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Transfer Budget (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.transfer_budget}
                    onChange={(e) => setFormData({ ...formData, transfer_budget: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., 100000000"
                  />
                </div>
              </div>
            </div>

            {/* Primary Contact Details */}
            <div className="pt-6 border-t border-white/5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold uppercase tracking-tight text-white">Primary Contact</h2>
                {formData.contacts.filter(c => c.name.trim() !== '').length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-widest text-zinc-500">Switch to:</span>
                    <select
                      data-testid="swap-primary-contact-select"
                      value=""
                      onChange={(e) => swapPrimaryContact(e.target.value)}
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-sm text-white outline-none"
                    >
                      <option value="">Select contact...</option>
                      {formData.contacts.map((c, i) => c.name.trim() !== '' ? (
                        <option key={i} value={i}>{c.name}{c.position ? ` (${POSITION_OPTIONS.find(p => p.value === c.position)?.label || c.position})` : ''}</option>
                      ) : null)}
                    </select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., John Smith"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Position
                  </label>
                  <select
                    value={formData.contact_position}
                    onChange={(e) => setFormData({ ...formData, contact_position: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    {POSITION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., contact@club.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., +44 20 1234 5678"
                  />
                </div>
              </div>
            </div>

            {/* Additional Contacts */}
            <div className="pt-6 border-t border-white/5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
                  <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
                  Additional Contacts
                  <span className="text-sm font-normal text-zinc-500">({formData.contacts.length}/8)</span>
                </h2>
                <button
                  type="button"
                  onClick={addContact}
                  disabled={formData.contacts.length >= 8}
                  data-testid="add-contact-button"
                  className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-4 py-2 text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} />
                  Add Contact
                </button>
              </div>

              {formData.contacts.length === 0 ? (
                <div className="text-center py-8 text-zinc-500 border border-dashed border-white/10 rounded-lg">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1} />
                  <p>No additional contacts added yet</p>
                  <p className="text-sm mt-1">Click "Add Contact" to add up to 8 contacts</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.contacts.map((contact, index) => (
                    <div
                      key={index}
                      className="p-5 bg-zinc-900/30 border border-white/5 rounded-lg"
                      data-testid={`contact-card-${index}`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400">
                          Contact #{index + 2}
                        </h3>
                        <button
                          type="button"
                          onClick={() => removeContact(index)}
                          data-testid={`remove-contact-${index}`}
                          className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-sm transition-colors"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                            Name
                          </label>
                          <input
                            type="text"
                            value={contact.name}
                            onChange={(e) => updateContact(index, 'name', e.target.value)}
                            className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                            placeholder="Contact name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                            Position
                          </label>
                          <select
                            value={contact.position}
                            onChange={(e) => updateContact(index, 'position', e.target.value)}
                            className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-2.5 text-white outline-none transition-all duration-200"
                          >
                            {POSITION_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                            Email
                          </label>
                          <input
                            type="email"
                            value={contact.email}
                            onChange={(e) => updateContact(index, 'email', e.target.value)}
                            className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                            placeholder="Email address"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={contact.phone}
                            onChange={(e) => updateContact(index, 'phone', e.target.value)}
                            className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                            placeholder="Phone number"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Additional Notes</h2>
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                  Notes
                </label>
                <textarea
                  rows="5"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  placeholder="Any additional notes about the club, partnership history, interests..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-white/5 flex gap-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="save-club-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" strokeWidth={2} />
                {loading ? 'Saving...' : isEdit ? 'Update Club' : 'Create Club'}
              </button>
              <Link
                to="/clubs"
                className="px-6 py-3 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm transition-all duration-200 uppercase tracking-wide font-bold"
              >
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClubForm;
